import { AnalyticsOutlined, SupportRounded } from '@mui/icons-material'
import { Box, Card, CardActionArea, Typography, useTheme } from '@mui/material'
import _ from 'lodash'
import ApiClient from '../../backend/ApiClient'
import { IInsights } from '../../definitions/Overview'
import UsersManager from '../../managers/UsersManager'
import { useAppDispatch } from '../../store/Hooks'
import { changeRoute } from '../../store/slices/SessionSlice'
import SmartCard from '../controls/cards/SmartCard'
import Spotlight from '../controls/spotlight/Spotlight'
import StaleObserver from '../controls/stale/StaleObserver'
import useQuery, { buildQueryKey } from '../hooks/useQuery'
import Tile from '../layout/Tile'
import PermissionsWrapper from '../permissions/PermissionsWrapper'

export default function Overview() {
    const theme = useTheme()
    const dispatch = useAppDispatch()
    const { status, response } = useQuery({
        queryKey: buildQueryKey('getOverviewData'),
        queryFn: () => ApiClient.getOverviewData(),
        cache: true
    })
    const currentUser = UsersManager.getCurrentUser()
    if (!currentUser) return null
    const onClickCard = (metadata: any) => {
        if (metadata && metadata.type && metadata.type === 'navigate') {
            dispatch(changeRoute({ route: metadata.route, params: metadata.routeParams }))
        }
    }

    const getCaptionColor = (type: string) => {
        switch (type) {
            case 'Summary': return theme.palette.secondary.main
            // case 'Summary': return 'rgba(155, 96, 127, 1)'
            case 'Claims': return 'rgba(200, 170, 40, 1)'
            case 'Contracts': return 'rgba(33, 201, 108, 1)'
            case 'Reports': return 'rgba(200, 100, 70, 1)'
            default: return theme.palette.primary[500]
        }
    }

    const insightCard = (insight: IInsights, index: number) => {
        return (
            <CardActionArea key={insight.title} onClick={() => onClickCard(insight.metadata)} sx={{ flex: 1, height: 170, borderRadius: 2, maxWidth: `calc(100% / ${_.max([response?.insights?.length ?? 3, 3])} )` }}>
                <SmartCard className={'animate glow delay-' + (index + 1)} key={insight.title + index}>
                    <Typography variant='caption' textTransform='uppercase' color={getCaptionColor(insight.type)} fontWeight={600} whiteSpace='normal' overflow='hidden' textOverflow='ellipsis'>{insight.type}</Typography>
                    <Typography fontWeight='bold' mt={1} mb={1} whiteSpace='nowrap' overflow='hidden' textOverflow='ellipsis'>{insight.title}</Typography>
                    <Typography sx={{ display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden', textOverflow: 'ellipsis' }} variant='subtitle2' fontWeight={400}>{insight.body}</Typography>
                </SmartCard>
            </CardActionArea>
        )
    }

    const circleCard = (icon: JSX.Element, text: string, onClick: () => void) => {
        return (
            <Tile.Tile key={text} display='flex' style={{ flexDirection: 'column', alignItems: 'center' }}>
                <Card sx={{ border: 'none', height: 64, width: 64, borderRadius: '50%' }}>
                    <CardActionArea onClick={onClick} sx={{ height: '100%', width: '100%' }}>
                        <Box height='100%' width='100%' display='flex' justifyContent='center' alignItems='center'>
                            {icon}
                        </Box>
                    </CardActionArea>
                </Card>
                <Typography variant='caption' mt={1}>{text}</Typography>
            </Tile.Tile>
        )
    }

    return (
        <Tile.Container>
            <Tile.Tile flex={1} display='flex' style={{ justifyContent: 'center', alignItems: 'center' }}>
                <Tile.Column flex={1}>
                    <Typography variant='h2' fontWeight='bold' align='center'>
                        Welcome back,
                        {' '}
                        {currentUser.name}
                        {' '}
                        <span
                            className='wave'
                            onMouseOver={e => {
                                // Reset the animation
                                const el = e.target as any
                                el.style.animation = 'none'
                                setTimeout(() => {
                                    el.style.animation = null
                                }, 10)
                            }}>👋
                        </span>
                    </Typography>
                    <Typography variant='h6' fontWeight={400} align='center'>What would you like to do today?</Typography>
                    <Box flex={1} height={240} minHeight={240} position='relative'>
                        <Box position='absolute' height='100%' width='100%' zIndex={10} display='flex' justifyContent='center'>
                            <Box margin={10} minWidth='calc(var(--content-page-max-width)/2)'>
                                <Spotlight defaultClosed />
                            </Box>
                        </Box>
                    </Box>
                    <Tile.Row justifyContent='center'>
                        <Tile.Row justifyContent='space-around' spacing={3} flex={1} style={{ maxWidth: 'calc(var(--content-page-max-width)/2)' }}>
                            <PermissionsWrapper permission='contracts.view'>
                                <div>
                                    {circleCard(<AnalyticsOutlined color='primary' fontSize='large' />, 'Contracts', () => dispatch(changeRoute({ route: 'contracts' })))}
                                </div>
                            </PermissionsWrapper>
                            {circleCard(<SupportRounded color='primary' fontSize='large' />, 'View Support', () => null)}
                        </Tile.Row>
                    </Tile.Row>
                </Tile.Column>
            </Tile.Tile>
            <Tile.Row spacing={2} height={170} style={{ marginBottom: 16 }}>
                {(response?.insights ?? []).map((insight, i) => (
                    <PermissionsWrapper key={i} permission='contracts.view'>
                        {insightCard(insight, i)}
                    </PermissionsWrapper>
                )
                )}
            </Tile.Row>
            <StaleObserver status={status} />
        </Tile.Container>
    )
}
